import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, Eye, EyeOff, CheckCircle2, KeyRound } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const TEAL = '#3fa758';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email }, { withCredentials: true });
      setSent(true);
    } catch (err) {
      // Even on error, show success to avoid email enumeration
      const d = err?.response?.data?.detail;
      if (typeof d === 'string' && d.toLowerCase().includes('email')) {
        setError(d);
      } else {
        setSent(true);
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8" style={{ color: TEAL }} />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-foreground">Forgot your password?</h1>
          <p className="text-sm text-muted-foreground mt-2">Enter your email and we&apos;ll send you a reset link.</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: '#16A34A' }} />
              <p className="text-sm font-semibold text-foreground">Check your inbox.</p>
              <p className="text-xs text-muted-foreground mt-1">If an account exists for that email, a reset link has been sent. It expires in 1 hour.</p>
              <Link to="/login" className="inline-block mt-5 text-xs font-semibold" style={{ color: TEAL }}>Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-500">{error}</div>}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@practice.com.au" data-testid="forgot-email"
                    className="w-full pl-10 pr-4 py-3 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3fa758]/30 focus:border-[#3fa758]" />
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-submit"
                className="w-full py-3 text-sm font-semibold text-white rounded-xl disabled:opacity-40"
                style={{ backgroundColor: TEAL }}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
              <Link to="/login" className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground pt-1">
                <ArrowLeft className="w-3 h-3" /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    if (pw.length < 8) { setError('Password must be at least 8 characters'); setLoading(false); return; }
    if (pw !== pw2) { setError('Passwords do not match'); setLoading(false); return; }
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pw }, { withCredentials: true });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to reset password');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8" style={{ color: TEAL }} />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-foreground">Set a new password</h1>
          <p className="text-sm text-muted-foreground mt-2">Your new password must be at least 8 characters.</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6">
          {done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: '#16A34A' }} />
              <p className="text-sm font-semibold text-foreground">Password reset.</p>
              <p className="text-xs text-muted-foreground mt-1">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-500" data-testid="reset-error">{error}</div>}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">New password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input required type={showPw ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} data-testid="reset-pw1"
                    placeholder="At least 8 characters"
                    className="w-full pl-10 pr-10 py-3 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3fa758]/30 focus:border-[#3fa758]" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Confirm new password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input required type={showPw ? 'text' : 'password'} value={pw2} onChange={e => setPw2(e.target.value)} data-testid="reset-pw2"
                    placeholder="Re-enter password"
                    className="w-full pl-10 pr-4 py-3 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3fa758]/30 focus:border-[#3fa758]" />
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="reset-submit"
                className="w-full py-3 text-sm font-semibold text-white rounded-xl disabled:opacity-40"
                style={{ backgroundColor: TEAL }}>
                {loading ? 'Saving…' : 'Reset password'}
              </button>
              <Link to="/login" className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground pt-1">
                <ArrowLeft className="w-3 h-3" /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
